import { Head } from '@inertiajs/react';
import { useState, useMemo } from 'react';
import { MainLayout } from '../../layouts/MainLayout';
import { WholesaleHero } from '../../components/wholesale-pricing/WholesaleHero';
import { WholesalePricingSection } from '../../components/wholesale-pricing/WholesalePricingSection';
import { FinalCTA } from '../../components/wholesale-pricing/FinalCTA';
import { PriceListModal } from '../../components/wholesale-pricing/PriceListModal';
import { getCSRFToken } from '../../utils/csrf';
import type { WholesalePricingProps, WholesaleTier } from '../../types/wholesale-pricing';

const FALLBACK_TIER: WholesaleTier = { id: 0, name: 'Mayorista', percentage: -3, min_volume: 200000, max_volume: 499999 };

function getActiveTier(volume: number, tiers: WholesaleTier[]): WholesaleTier {
  if (!tiers.length) return FALLBACK_TIER;
  for (let i = tiers.length - 1; i >= 0; i--) {
    if (volume >= tiers[i].min_volume) return tiers[i];
  }
  return tiers[0];
}

export default function WholesalePricing({ tiers = [], products = [], appUrl, loginUrl, whatsappUrl, isAuthenticated }: WholesalePricingProps) {
  const [volume, setVolume] = useState(500000);
  const activeTier = useMemo(() => getActiveTier(volume, tiers), [volume, tiers]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalSuccess, setModalSuccess] = useState(false);

  const downloadUrl = `/precios-mayoristas/descargar?volume=${volume}`;

  const handleDownloadClick = () => {
    if (isAuthenticated) {
      window.location.href = downloadUrl;
    } else {
      setIsModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalSuccess(false);
  };

  const handleModalSubmit = async (data: { businessName: string; email: string; phone: string }) => {
    setIsSubmitting(true);
    try {
      const response = await fetch('/precios-mayoristas/solicitar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
        },
        body: JSON.stringify({
          ...data,
          volume,
          tier: activeTier.name,
        }),
      });

      if (response.ok) {
        setModalSuccess(true);
      } else {
        throw new Error('Error al enviar');
      }
    } catch (error) {
      alert('Hubo un error. Intentá de nuevo o contactanos por WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <Head>
        <title>Precios Mayoristas | Abasto Virtual</title>
        <meta
          name="description"
          content="Explorá nuestros precios mayoristas. Descuentos de hasta 8% según tu volumen de compra. Frutas, verduras y más."
        />
        <link rel="canonical" href={`${appUrl}/precios-mayoristas`} />
      </Head>

      <WholesaleHero onDownloadClick={handleDownloadClick} />
      <WholesalePricingSection
        volume={volume}
        onVolumeChange={setVolume}
        tiers={tiers}
        activeTier={activeTier}
        products={products}
        isAuthenticated={isAuthenticated}
        downloadUrl={downloadUrl}
        onRequestPriceList={() => setIsModalOpen(true)}
      />
      <FinalCTA onDownloadClick={handleDownloadClick} />

      <PriceListModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        isSubmitting={isSubmitting}
        success={modalSuccess}
      />
    </MainLayout>
  );
}
